import type { Component } from 'solid-js';
import { Router } from '@solidjs/router';
import { MetaProvider, Title } from '@solidjs/meta';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/solid-query';
import { onQueryError, onQueryFetch } from '~/shared/api/forbidden';
import { routes } from '~/app/router';
import { mainQueryResolver } from '~/app/page-main-query';
import { AuthProvider } from '~/shared/api/auth-context';
import { I18nProvider } from '~/shared/lib/i18n';
import { ThemeProvider } from '~/shared/lib/theme';
import { ToastProvider } from '~/shared/ui/Toast';
import { CommandPaletteProvider } from '~/widgets/shell/CommandPalette';
import { OfflineBanner } from '~/shared/ui/OfflineBanner';
import { AppErrorBoundary } from '~/app/ErrorBoundary';
import { retryQuery, retryQueryDelay } from '@sportpark/shared/query-policy';
import { RealtimeSyncProvider } from '~/shared/realtime';

const mainQueryOf = mainQueryResolver(routes);
const queryCache = new QueryCache({
  onError: (error, query) => onQueryError(error, query, window.location.pathname, mainQueryOf),
});
queryCache.subscribe((event) => {
  if (event.type === 'updated' && event.action.type === 'fetch') onQueryFetch(event.query, window.location.pathname);
});

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: retryQuery,
      retryDelay: retryQueryDelay,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

export const App: Component = () => {
  return (
    <MetaProvider>
      <I18nProvider>
        <ThemeProvider>
          <Title>SportPark — панель управления</Title>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <RealtimeSyncProvider>
                <ToastProvider>
                  <CommandPaletteProvider>
                    <AppErrorBoundary>
                      <OfflineBanner />
                      <Router>{routes}</Router>
                    </AppErrorBoundary>
                  </CommandPaletteProvider>
                </ToastProvider>
              </RealtimeSyncProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </I18nProvider>
    </MetaProvider>
  );
};
